import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ConfirmDialog.css';

// Plain strings keep the window.confirm habit of separating paragraphs with
// blank lines, so render those as real paragraphs rather than one run-on block.
function renderMessage(message) {
  if (message === null || message === undefined || message === '') return null;
  if (typeof message !== 'string') return <div className="zc-message">{message}</div>;
  return message
    .split(/\n{2,}/)
    .map((para, i) => (
      <p key={i} className="zc-message">
        {para.split('\n').map((line, j, lines) => (
          <React.Fragment key={j}>
            {line}
            {j < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    ));
}

/**
 * Presentational confirm/alert card. Most callers should reach for `useConfirm()`
 * instead of mounting this directly — see ConfirmProvider.
 *
 * `tone` only changes the primary button: 'danger' turns it red for actions that
 * can't be undone, everything else stays near-black. The copy carries the rest.
 */
export default function ConfirmDialog({
  open,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Continue',
  cancelLabel = 'Cancel',
  tone = 'default',
  showCancel = true,
  busy = false,
  onConfirm,
  onCancel,
}) {
  const confirmRef = useRef(null);
  const restoreFocusRef = useRef(null);

  const cancel = useCallback(() => {
    if (busy) return;
    if (onCancel) onCancel();
  }, [busy, onCancel]);

  // Park the caller's focus, move it to the primary action, and hand it back on
  // close so keyboard users land where they left off.
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement;
    const raf = window.requestAnimationFrame(() => confirmRef.current?.focus());
    return () => {
      window.cancelAnimationFrame(raf);
      const el = restoreFocusRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    };
  }, [open]);

  // Esc cancels, matching the native dialog it replaces. Capture phase so the
  // key doesn't also close the Bootstrap modal underneath.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      cancel();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, cancel]);

  // Stop the page behind the dialog from scrolling under it.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="zc-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) cancel(); }}
    >
      <div
        className="zc-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="zc-title"
        aria-describedby={message ? 'zc-desc' : undefined}
      >
        <div className="zc-body">
          <h2 className="zc-title" id="zc-title">{title}</h2>
          <div id="zc-desc">{renderMessage(message)}</div>
        </div>

        <div className="zc-footer">
          {showCancel && (
            <button
              type="button"
              className="zc-btn zc-btn--ghost"
              onClick={cancel}
              disabled={busy}
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            ref={confirmRef}
            className={`zc-btn zc-btn--${tone === 'danger' ? 'danger' : 'default'}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
